import { dom } from '@omegagrid/core';
import { msg } from '@omegagrid/localize';
import rp, { AppComponent, Application, ParsedComponentId, GridEditor, SqlEditor, SqlEditorResult, SqlQueryPlan, SqlEditorEvent, SqlEditorQueryPlan } from '@reporting/rp-app-host';
import { Sidebar } from './components';
import { ValuesGridEditorAdapter, DwhSqlEditorAdapter, ProcessGridAdapter } from './adapters';
import { DwhObjectType } from './types';
import { DbView, DbTable } from '@reporting/rp-types/server';

const createMainSidebar = (app: Application, _componentId: ParsedComponentId) => {
	return dom.createElement<Sidebar>('rp-dwh-sidebar');
}

const createValuesGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const kind = componentId.ids[1] as 'table' | 'view';
	const idObject = parseInt(componentId.ids[2]);
	const object = await rp.ms('server').get<DbTable | DbView>(`${kind === 'view' ? 'views' : 'tables'}/${idObject}`);
	if (!object) throw new Error('Object not found');
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new ValuesGridEditorAdapter(object);
	return component;
}

const createProcessGridEditor = async (app: Application, _componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new ProcessGridAdapter();
	return component;
}

const createSqlEditor = async (app: Application, componentId: ParsedComponentId) => {
	const subType = componentId.ids[1];
	const id = componentId.ids[2];
	const component = dom.createElement<SqlEditor>('rp-editor-sql');
	if (subType === 'buffer') {
		component.adapter = new DwhSqlEditorAdapter('processBuffer', null, id);
	} else {
		component.adapter = new DwhSqlEditorAdapter('object', subType as DwhObjectType, id);
		component.addEventListener('sqleditor.beforeExec', createSqlEditorBeforeExecHandler(component, app, componentId));
		component.addEventListener('sqleditor.result',  createSqlEditorResultHandler(component, app, componentId));
	}
	return component;
}

const createSqlEditorBeforeExecHandler = (component: AppComponent, app: Application, componentId: ParsedComponentId) => {
	return () => {
		const resultId = `${componentId.type}-sqlResult-${componentId.ids[1]}`;
		if (app.host.components.has(resultId)) {
			(app.host.components.components.get(resultId) as SqlEditorResult).clear();
		}
	}
}

const createSqlEditorResultHandler = (component: AppComponent, app: Application, componentId: ParsedComponentId) => {
	// datasets and messages in stream order - a warning belongs in front of the dataset it preceded
	const results: SqlEditorEvent[] = [];
	const plans: SqlQueryPlan[] = [];
	let resultPromise: Promise<SqlEditorResult> = null;
	const tabColor = rp.settings.get('result.tab.color') === '0' ? null : rp.settings.get('result.tab.color');

	return async (e: SqlEditorEvent) => {
		if (e.dataset || e.message) results.push(e);
		if (e.queryPlan) plans.push(e.queryPlan);

		if (e.dataset || e.message) {
			const resultId = `${componentId.type}-sqlResult-${componentId.ids[1]}`;
			const openOptions = { title: `${component.name ?? ''} [result]`.trimStart(), color: tabColor, persistent: false };
			let editorResult = app.host.components?.components.get(resultId) as SqlEditorResult;
			if (editorResult) {
				app.host.open(resultId, openOptions);
			} else {
				if (!resultPromise || !app.host.components?.has(resultId)) {
					resultPromise = app.host.open(resultId, openOptions) as Promise<SqlEditorResult>;
				}
				editorResult = await resultPromise;
			}
			if (!editorResult) return;

			let result: SqlEditorEvent;
			while (result = results.shift()) {
				if (result.dataset) {
					if (result.dataset.index == 0) editorResult.clear();
					editorResult.addDataset(result.dataset);
				} else {
					editorResult.addMessage(result.message);
				}
			}
		}

		if (e.queryPlan) {
			const resultId = `${componentId.type}-sqlQueryPlan-${componentId.ids[1]}`;
			const editorQueryPlan = (await app.host.open(resultId, {
				title: `${component.name} [plan]`,
				color: tabColor,
				persistent: false
			})) as SqlEditorQueryPlan;
			let plan: SqlQueryPlan;
			while (plan = plans.shift()) {
				if (plan.index == 0) editorQueryPlan.clear();
				editorQueryPlan.addPlan(plan);
			}
		}
	}
}

const createSqlEditorResult = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<SqlEditorResult>('rp-editor-sql-result');
}

const createSqlEditorQueryPlan = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<SqlEditorResult>('rp-editor-sql-plan');
}

export const componentFactories = new Map<string, (app: Application, componentId: ParsedComponentId) => AppComponent | Promise<AppComponent>>([
	['dwh', createMainSidebar],
	['values', createValuesGridEditor],
	['processes', createProcessGridEditor],
	['sql', createSqlEditor],
	['sqlResult', createSqlEditorResult],
	['sqlQueryPlan', createSqlEditorQueryPlan],
]);
